
<template>
  <div class="theme-switcher">
    <!-- Floating Trigger -->
    <button
      type="button"
      class="theme-switcher-trigger"
      :aria-expanded="isOpen"
      aria-label="Pilih tema"
      @click.stop="toggleDropdown"
    >
      <!-- Light Icon -->
      <svg
        v-if="currentTheme === 'light'"
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <circle cx="12" cy="12" r="4" />
        <path
          d="M12 2v2m0 16v2M4.93 4.93l1.42 1.42m11.3 11.3 1.42 1.42M2 12h2m16 0h2M4.93 19.07l1.42-1.42m11.3-11.3 1.42-1.42"
        />
      </svg>

      <!-- Dark Icon -->
      <svg
        v-else
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.8"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <path
          d="M20.985 12.486a9 9 0 1 1-9.473-9.472c.405-.022.617.46.402.803a6.5 6.5 0 0 0 8.268 8.268c.344-.215.825-.003.803.401"
        />
      </svg>
    </button>

    <!-- Dropdown -->
    <Transition name="theme-dropdown">
      <div
        v-if="isOpen"
        class="theme-switcher-dropdown"
        @click.stop
      >
        <button
          type="button"
          class="theme-option"
          :class="{ selected: currentTheme === 'light' }"
          @click="setTheme('light')"
        >
          <span class="theme-option-icon">☀</span>
          <span>Light Mode</span>

          <svg
            v-if="currentTheme === 'light'"
            class="theme-option-check"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m5 12 4 4L19 6" />
          </svg>
        </button>

        <button
          type="button"
          class="theme-option"
          :class="{ selected: currentTheme === 'dark' }"
          @click="setTheme('dark')"
        >
          <span class="theme-option-icon">☾</span>
          <span>Dark Mode</span>

          <svg
            v-if="currentTheme === 'dark'"
            class="theme-option-check"
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="m5 12 4 4L19 6" />
          </svg>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script>
import {
  themeState,
  initTheme,
  setTheme
} from '@/utils/theme'

export default {
  name: 'ThemeToggle',

  data() {
    return {
      isOpen: false
    }
  },

  computed: {
    currentTheme() {
      return themeState.currentTheme
    }
  },

 
  async mounted() {
    await initTheme()

    document.addEventListener('click', this.handleOutsideClick)
    document.addEventListener('keydown', this.handleEscape)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    document.removeEventListener('keydown', this.handleEscape)
  },


  methods: {
    toggleDropdown() {
      this.isOpen = !this.isOpen
    },

    setTheme(theme) {
      setTheme(theme)
      this.isOpen = false
    },

    handleOutsideClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isOpen = false
      }
    },

    handleEscape(event) {
      if (event.key === 'Escape') {
        this.isOpen = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.theme-switcher {
  position: fixed;
  right: 24px;
  bottom: 92px; // di atas tombol WhatsApp
  left: auto;
  z-index: 1300;

  display: inline-flex;
  align-items: center;
}
/* Floating Button */
.theme-switcher-trigger {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 56px;
  height: 56px;
  padding: 0;

  color: var(--color-text);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 50%;

  box-shadow: var(--box-shadow);
  cursor: pointer;

  transition:
    transform 0.2s ease,
    background 0.2s ease,
    color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    transform: translateY(-3px);
    background: var(--color-surface-hover);
  }

  &:active {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--color-text);
    outline-offset: 4px;
  }
}

/* Dropdown */
.theme-switcher-dropdown {
  position: absolute;
  right: 0;
  bottom: calc(100% + 12px);
  z-index: 1;

  width: 190px;
  padding: 6px;

  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-lg);
  box-shadow: var(--box-shadow);
}

.theme-option {
  display: flex;
  align-items: center;
  gap: var(--spacing-md);

  width: 100%;
  min-height: 44px;
  padding: 0 10px;

  color: var(--color-text);
  background: transparent;
  border: 0;
  border-radius: var(--radius-sm);

  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  text-align: left;
  cursor: pointer;

  transition:
    color var(--transition-fast),
    background var(--transition-fast);

  span:nth-child(2) {
    flex: 1;
  }

  &:hover,
  &.selected {
    color: var(--color-text);
    background: var(--color-surface-hover);
  }
}

.theme-option-icon {
  width: 20px;
  font-size: 20px;
  text-align: center;
}

.theme-option-check {
  margin-left: auto;
}

/* Dropdown Animation */
.theme-dropdown-enter-active,
.theme-dropdown-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.theme-dropdown-enter-from,
.theme-dropdown-leave-to {
  opacity: 0;
  transform: translateY(8px);
}

/* Mobile */
@media (max-width: 768px) {
  .theme-switcher {
    right: 16px;
    bottom: 78px;
    left: auto;
  }

  .theme-switcher-trigger {
    width: 50px;
    height: 50px;
  }

  .theme-switcher-dropdown {
    width: 180px;
  }
}
</style>